/**
 * 协议签署 Hook
 * 用于处理协议签署操作
 */

import { useState, useCallback } from "react";
import { useConsentStore } from "@/stores/consentStore";
import { agreementService } from "@/services/agreementService";

/** 最大重试次数 */
const MAX_RETRIES = 3;

/** 重试延迟（毫秒） */
const RETRY_DELAY = 1000;

/**
 * 协议签署 Hook
 *
 * 功能：
 * - 签署协议
 * - 处理签署错误
 * - 签署成功后清除待签署列表
 * - 支持重试机制
 */
export function useAgreementSign() {
  const [isSigning, setIsSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { clearPendingAgreements, closeModal, setLastCheckedAt } =
    useConsentStore();

  /**
   * 延迟函数
   */
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * 签署协议（带重试）
   */
  const signWithRetry = async (
    agreementIds: number[],
    currentRetry: number
  ): Promise<boolean> => {
    try {
      await agreementService.signAgreements(agreementIds);
      return true;
    } catch (err) {
      // 记录错误日志
      console.error(
        `签署协议失败 (尝试 ${currentRetry + 1}/${MAX_RETRIES}):`,
        err
      );

      // 如果还有重试次数，则重试
      if (currentRetry < MAX_RETRIES - 1) {
        setRetryCount(currentRetry + 1);
        await delay(RETRY_DELAY * (currentRetry + 1)); // 指数退避
        return signWithRetry(agreementIds, currentRetry + 1);
      }

      throw err;
    }
  };

  /**
   * 签署协议
   * @param agreementIds 要签署的协议 ID 列表
   * @returns 是否签署成功
   */
  const signAgreements = useCallback(
    async (agreementIds: number[]): Promise<boolean> => {
      if (agreementIds.length === 0) {
        setError("没有需要签署的协议");
        return false;
      }

      setIsSigning(true);
      setError(null);
      setRetryCount(0);

      try {
        await signWithRetry(agreementIds, 0);
        clearPendingAgreements();
        closeModal();
        // 重置缓存时间，下次检查时会重新获取
        setLastCheckedAt(Date.now());
        return true;
      } catch (err) {
        // 记录最终错误
        console.error("签署协议最终失败:", err);

        const message = err instanceof Error ? err.message : "签署失败，请重试";
        setError(message);
        return false;
      } finally {
        setIsSigning(false);
      }
    },
    [clearPendingAgreements, closeModal, setLastCheckedAt]
  );

  /**
   * 清除错误信息
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    signAgreements,
    isSigning,
    error,
    clearError,
    retryCount,
  };
}

export default useAgreementSign;
